
import { 
  authenticateWithWordPress, 
  startWordPressBackup,
  getBackupStatus,
  restoreFromBackup,
  type WordPressCredentials,
  type BackupResult
} from "@/utils/wordpressApi";
import {
  initDatabase,
  getAllWebsites,
  getWebsiteById as dbGetWebsiteById,
  saveWebsite,
  updateLastBackup,
  seedDemoData
} from "./databaseService";
import { 
  getCredentials, 
  saveCredentials as saveCredentialsToDb 
} from "./credentialService";
import { 
  saveBackupToSupabase,
  createSimulatedBackupFile
} from "./supabaseBackupService";
import { toast } from "@/hooks/use-toast";

// Type definition for a website
export interface Website {
  id: string;
  title: string;
  url: string;
  platform: 'wordpress' | 'shopify' | 'woocommerce';
  lastBackup: string;
  nextBackup: string;
  storageUsed: number;
  status: 'active' | 'paused';
  credentials?: WordPressCredentials;
}

// Export WordPressCredentials type for use in other files
export type { WordPressCredentials };

/**
 * Saves WordPress credentials for a website
 */
export const saveWebsiteCredentials = async (
  websiteId: string,
  credentials: WordPressCredentials
): Promise<void> => {
  const success = await saveCredentialsToDb(websiteId, credentials);
  if (!success) {
    throw new Error('Failed to save credentials to database');
  }
  console.log(`📝 CREDENTIALS: Credentials saved for website ${websiteId}`);
}

/**
 * Gets WordPress credentials for a website
 */
export const getWebsiteCredentials = async (websiteId: string): Promise<WordPressCredentials | null> => {
  return await getCredentials(websiteId);
}

/**
 * Initiates a backup for a WordPress website
 */
export const backupWebsite = async (website: Website): Promise<BackupResult | null> => {
  console.log("===========================================");
  console.log("🎯 BACKUP PROCESS: STARTING BACKUP PROCESS");
  console.log("===========================================");
  console.log("🎯 BACKUP PROCESS: Website details:", {
    id: website.id,
    title: website.title,
    url: website.url,
    platform: website.platform
  });
  
  try {
    let credentials: WordPressCredentials | null = null;
    
    // For site ID 4 (Drive Expert), use pre-defined credentials
    if (website.id === "4") {
      console.log("🔑 CREDENTIALS: Using pre-configured credentials for Drive Expert site");
      credentials = {
        siteUrl: "https://www.drive-expert.com",
        username: "driveexp",
        password: "##Test$$Blog##"
      };
      
      console.log("🔑 CREDENTIALS: Drive Expert credentials:", {
        siteUrl: credentials.siteUrl,
        username: credentials.username,
        passwordLength: credentials.password.length
      });
      
      // Save these credentials for future use
      try {
        await saveWebsiteCredentials(website.id, credentials);
        console.log("🔑 CREDENTIALS: Drive Expert credentials saved successfully");
      } catch (saveError) {
        console.error("🔑 CREDENTIALS: Failed to save Drive Expert credentials:", saveError);
        // Continue with backup even if credential saving fails
      }
    } else {
      // For all other sites, get stored credentials
      console.log("🔑 CREDENTIALS: Retrieving stored credentials for website:", website.id);
      credentials = await getWebsiteCredentials(website.id);
    }
    
    if (!credentials) {
      console.error("❌ CREDENTIALS: No credentials available for backup");
      
      toast({
        title: "Backup failed",
        description: `No credentials found for ${website.title}. Please add credentials first.`,
        variant: "destructive",
      });
      
      return null;
    }
    
    console.log("✅ CREDENTIALS: Credentials retrieved successfully");
    console.log("🔑 CREDENTIALS: Site URL:", credentials.siteUrl);
    console.log("🔑 CREDENTIALS: Username:", credentials.username);
    console.log("🔑 CREDENTIALS: Password length:", credentials.password.length);
    
    // Test WordPress authentication first
    console.log("🔐 AUTHENTICATION: Testing WordPress authentication...");
    const isAuthenticated = await authenticateWithWordPress(credentials);
    
    if (!isAuthenticated) {
      console.warn("⚠️  AUTHENTICATION: WordPress authentication failed, but continuing with backup attempt");
      console.warn("⚠️  AUTHENTICATION: This might indicate wrong credentials or site issues");
    } else {
      console.log("✅ AUTHENTICATION: WordPress authentication successful");
    }
    
    // Start the WordPress backup
    console.log("🚀 BACKUP: Starting WordPress backup process...");
    const backupResult = await startWordPressBackup(website.id, credentials);
    
    console.log("📊 BACKUP: Backup result received:", backupResult);
    
    if (backupResult) {
      console.log("✅ BACKUP: Backup initiated successfully");
      console.log("📝 BACKUP: Backup details:", {
        id: backupResult.id,
        status: backupResult.status,
        size: backupResult.size,
        createdAt: backupResult.createdAt
      });
      
      // Save backup record to Supabase
      console.log("💾 SUPABASE: Saving backup record to Supabase...");
      const supabaseSaved = await saveBackupToSupabase(website.id, backupResult);
      
      if (supabaseSaved) {
        console.log("✅ SUPABASE: Backup record saved to Supabase successfully");
      } else {
        console.warn("⚠️ SUPABASE: Failed to save backup record to Supabase, but backup is still valid");
      }
      
      // Update last backup time in local database
      await updateLastBackup(website.id, "Just now");
      console.log("📝 DATABASE: Updated last backup time in local database");
      
      toast({
        title: "Backup successful!",
        description: `Your ${website.title} backup has been completed. Check backup history for details.`,
      });
      
      console.log("🎉 BACKUP: Process completed successfully!");
      return backupResult;
    } else {
      console.error("❌ BACKUP: WordPress backup returned null result");
      
      toast({
        title: "Backup failed",
        description: `Failed to start backup for ${website.title}. Please check the console for details.`,
        variant: "destructive",
      });
      
      return null;
    }
    
  } catch (error) {
    console.error(`❌ BACKUP ERROR: Error during backup for website ${website.id}:`, error);
    console.error("❌ BACKUP ERROR: Error details:", {
      name: error instanceof Error ? error.name : 'Unknown',
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined
    });
    
    // More detailed error message
    let errorMessage = "There was an error starting your backup.";
    if (error instanceof Error) {
      errorMessage += ` Error: ${error.message}`;
    }
    
    toast({
      title: "Backup failed",
      description: errorMessage,
      variant: "destructive",
    });
    
    throw error; // Re-throw to handle in the calling function
  } finally {
    console.log("===========================================");
    console.log("🏁 BACKUP PROCESS: BACKUP PROCESS COMPLETED");
    console.log("===========================================");
  }
}

export const restoreWebsite = async (
  website: Website, 
  backupId: string
): Promise<boolean> => {
  const credentials = await getWebsiteCredentials(website.id);
  
  if (!credentials) {
    console.error("No credentials found for website", website.id);
    
    toast({
      title: "Restore failed",
      description: "No credentials found for this website. Please add credentials first.",
      variant: "destructive",
    });
    
    return false;
  }
  
  try {
    const result = await restoreFromBackup(website.id, backupId, credentials);
    
    if (result) {
      toast({
        title: "Restore initiated",
        description: "Your website restore is now in progress.",
      });
    } else {
      toast({
        title: "Restore failed",
        description: "Failed to initiate restore. Please try again.",
        variant: "destructive",
      });
    }
    
    return result;
  } catch (error) {
    console.error(`Error during restore for website ${website.id}:`, error);
    
    toast({
      title: "Restore failed",
      description: "There was an error during the restore process. Please try again.",
      variant: "destructive",
    });
    
    return false;
  }
}

export const setupSampleWebsites = async (): Promise<void> => {
  try {
    // Initialize database
    await initDatabase();
    
    // Seed demo data
    await seedDemoData();
    
    // Ensure Drive Expert site has proper credentials
    const driveExpertCredentials: WordPressCredentials = {
      siteUrl: "https://www.drive-expert.com",
      username: "driveexp",
      password: "##Test$$Blog##"
    };
    
    await saveWebsiteCredentials("4", driveExpertCredentials);
    
  } catch (error) {
    console.error("Error setting up sample websites:", error);
  }
}

/**
 * Get website by ID
 */
export const getWebsiteById = async (websiteId: string): Promise<Website | undefined> => {
  const website = await dbGetWebsiteById(websiteId);
  return website || undefined;
}

/**
 * Get all websites
 */
export const getAllWebsitesFromDb = async (): Promise<Website[]> => {
  return await getAllWebsites();
}
